import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import AccountPage from '../components/AccountPage'
import subscriptionsController, {
  ISubscriptionResponse,
} from '../../api/controllers/subscriptions.controller'
import { signOut } from '../../auth/actions'
import { isRegularUser } from '../../utils/jwtToken'
import { getUserInterviews as getUserInterviewsApi } from '../../api/controllers/user-interviews.controller'

const AccountContainer: React.FC = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const [accountData, setAccountData] = useState<ISubscriptionResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [interviewsLoading, setInterviewsLoading] = useState(false)
  const [interviews, setInterviews] = useState<{ regular_interviews: any[]; ava_interviews: any[] } | null>(null)
  
  const isRegular = isRegularUser()

  const fetchAccountData = useCallback(async () => {
    try {
      setLoading(true)
      const data = await subscriptionsController.getSubscription()
      setAccountData(data)
    } catch (err) {
      console.error('Error fetching account data:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAccountData()
  }, [fetchAccountData])

  // Fetch interviews for regular users
  const fetchInterviews = useCallback(async () => {
    if (!isRegular) return
    
    try {
      setInterviewsLoading(true)
      const data = await getUserInterviewsApi()
      setInterviews(data)
    } catch (err) {
      console.error('Error fetching interviews:', err)
    } finally {
      setInterviewsLoading(false)
    }
  }, [isRegular])

  useEffect(() => {
    if (isRegular) {
      fetchInterviews()
    }
  }, [isRegular, fetchInterviews])

  // Check for message in URL query params
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const messageParam = urlParams.get('message')
    if (messageParam === 'interview_limit_reached') {
      setMessage('You have reached your interview limit for this month. Please upgrade your subscription or wait for your limit to reset.')
      // Remove message from URL
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [location.search])

  // Refresh data when returning from Stripe Portal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('portal_return') === 'true') {
      // Remove query parameter
      window.history.replaceState({}, '', window.location.pathname)
      // Refresh subscription data
      fetchAccountData()
    }
  }, [fetchAccountData])

  const handleManageSubscription = useCallback(async () => {
    try {
      setPortalLoading(true)
      const returnUrl = `${window.location.origin}/account?portal_return=true`
      const response = await subscriptionsController.createPortalSession(returnUrl)
      // Redirect to Stripe Customer Portal
      window.location.href = response.portal_url
    } catch (err) {
      console.error('Error creating portal session:', err)
      setPortalLoading(false)
    }
  }, [])

  const handleLogout = useCallback(() => {
    dispatch(signOut())
  }, [dispatch])

  if (loading) {
    return <div>Loading...</div>
  }

  if (!accountData) {
    return <div>Error loading account data</div>
  }

  const user = accountData.user || {
    email: '',
    name: '',
  }

  return (
    <AccountPage
      user={user}
      subscription={accountData.subscription}
      interviewCount={accountData.interview_count}
      remainingInterviews={accountData.remaining_interviews}
      onManageSubscription={handleManageSubscription}
      onLogout={handleLogout}
      loading={portalLoading}
      message={message}
      interviews={isRegular ? interviews : null}
      interviewsLoading={interviewsLoading}
    />
  )
}

export default AccountContainer


